import { useState, type ReactNode } from "react";
import { normalizeOperationColumns } from "@/views/components/ProTable";

import {
    Button as ZhButton,
    Dialog as ZhDialog,
    Grid as ZhGrid,
    BaseItem as ZhBaseItem,
    Table as ZhTable,
    type TableColumnProps
} from "zhihao-ui-react";

export type TradeOrderData = Record<string, unknown>;

export interface TradeOrderField {
    label: string;
    key: string;
    value?: (data: TradeOrderData) => ReactNode;
}

export interface TradeOrderAction {
    label: string;
    onClick: () => void;
    disabled?: boolean;
    variant?: "button" | "link";
}

function displayValue(value: unknown): string {
    return value === undefined || value === null || value === "" ? "--" : String(value);
}

/** 订单子组件统一的字段展示，保留 zhihao-ui 的网格和字段语义。 */
export function TradeOrderInfoPanel({
    title,
    data = {},
    fields,
    actions = [],
    children
}: {
    title: string;
    data?: TradeOrderData;
    fields: TradeOrderField[];
    actions?: TradeOrderAction[];
    children?: ReactNode;
}) {
    return (
        <section className="section-card mb-4">
            <div className="flex items-center justify-between mb-4">
                <div className="section-title mb-0">{title}</div>
                <div className="flex gap-2">
                    {actions.map(action =>
                        action.variant === "link" ? (
                            <button
                                key={action.label}
                                className="text-btn"
                                disabled={action.disabled}
                                type="button"
                                onClick={action.onClick}
                            >
                                {action.label}
                            </button>
                        ) : (
                            <ZhButton key={action.label} type="primary" disabled={action.disabled} onClick={action.onClick}>
                                {action.label}
                            </ZhButton>
                        )
                    )}
                </div>
            </div>
            <ZhGrid cols={4} gap={[12, 16]}>
                {fields.map(field => (
                    <ZhBaseItem
                        key={field.key}
                        label={field.label}
                        value={field.value ? field.value(data) : displayValue(data[field.key])}
                    />
                ))}
            </ZhGrid>
            {children}
        </section>
    );
}

/** 订单子列表的最小业务表格，支持行点击和逐行操作回调。 */
export function TradeOrderListPanel({
    title,
    items,
    columns,
    onSelect,
    onAction
}: {
    title: string;
    items: TradeOrderData[];
    columns: TradeOrderField[];
    onSelect?: (item: TradeOrderData) => void;
    onAction?: (item: TradeOrderData) => void;
}) {
    // 交易订单子列表统一生成 zhihao-ui-react 列配置，包含操作列。
    const tableColumns: TableColumnProps<TradeOrderData>[] = [
        ...columns.map(column => ({
            key: column.key,
            label: column.label,
            prop: column.key,
            render: ({ row }: { row: TradeOrderData }) =>
                column.value ? column.value(row) : displayValue(row[column.key])
        })),
        ...(onAction
            ? [
                  {
                      key: "operation",
                      label: "操作",
                      render: ({ row }: { row: TradeOrderData }) => (
                          <ZhButton type="link" onClick={() => onAction(row)}>
                              处理
                          </ZhButton>
                      )
                  }
              ]
            : [])
    ];
    return (
        <section className="section-card mb-4">
            <div className="section-title mb-4">{title}</div>
            <div className="overflow-auto">
                <ZhTable<TradeOrderData>
                    columns={normalizeOperationColumns(tableColumns, items)}
                    data={items}
                    pagination={false}
                    rowKey={row => String(row.id ?? row.code ?? items.indexOf(row))}
                    onRowClick={row => onSelect?.(row)}
                />
            </div>
        </section>
    );
}

/** 订单操作弹窗，统一承载旧 Vue 弹窗的输入、确认和关闭事件。 */
export function TradeOrderActionDialog({
    open,
    title,
    message,
    initialValue = "",
    inputLabel = "说明",
    confirmText = "确认",
    onClose,
    onSubmit
}: {
    open: boolean;
    title: string;
    message?: ReactNode;
    initialValue?: string;
    inputLabel?: string;
    confirmText?: string;
    onClose: () => void;
    onSubmit: (value: string) => void;
}) {
    const [value, setValue] = useState(initialValue);
    return (
        <ZhDialog
            open={open}
            title={title}
            width={520}
            onCancel={onClose}
            footer={
                <div className="flex justify-end gap-2">
                    <ZhButton onClick={onClose}>取消</ZhButton>
                    <ZhButton type="primary" onClick={() => onSubmit(value)}>
                        {confirmText}
                    </ZhButton>
                </div>
            }
        >
            {message ? <div className="mb-4">{message}</div> : null}
            <label className="block">
                <span className="mb-2 block">{inputLabel}</span>
                <textarea
                    className="w-full rounded border border-gray-300 p-2"
                    value={value}
                    rows={3}
                    onChange={event => setValue(event.target.value)}
                />
            </label>
        </ZhDialog>
    );
}

/** 可展开的提货面板，复用真实的提货信息字段和切换回调。 */
export function TradeOrderCollapsiblePanel({
    title,
    data,
    fields,
    children,
    defaultOpen = false,
    onToggle
}: {
    title: string;
    data?: TradeOrderData;
    fields: TradeOrderField[];
    children?: ReactNode;
    defaultOpen?: boolean;
    onToggle?: (open: boolean) => void;
}) {
    const [open, setOpen] = useState(defaultOpen);
    const toggle = () => {
        const next = !open;
        setOpen(next);
        onToggle?.(next);
    };
    return (
        <section className="rounded border border-gray-200 p-4 mb-4">
            <button type="button" className="flex w-full items-center justify-between text-left" onClick={toggle}>
                <span className="font-medium">{title}</span>
                <span>{open ? "收起" : "展开"}</span>
            </button>
            {open ? <TradeOrderInfoPanel title="提货信息" data={data} fields={fields} children={children} /> : null}
        </section>
    );
}
